import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'

export type WeekDay = 'sat' | 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri'

export interface TenantWeek {
  id: number
  tenant_id: number
  year_no: number
  week_no: number
  start_date: string
  end_date: string
  created_at?: string
}

export interface UserRoute {
  id: number
  tenant_id: number
  client_id: number
  user_id: number
  store_id: number
  shift_id: number
  visit_type_id: number
  wk_no: number
  day: WeekDay
  category_ids: number[]
  subcat_ids: number[]
  brand_ids: number[]
  w_m_bm: number
  created_at: string
  updated_at: string
  store_name?: string | null
  region_name?: string | null
  city_name?: string | null
  store_format_name?: string | null
  chain_name?: string | null
  channel_name?: string | null
  client_name?: string | null
  user_name?: string | null
  user_username?: string | null
  shift_name?: string | null
  visit_type_name?: string | null
  category_names?: string[]
  subcat_names?: string[]
  brand_names?: string[]
}

export interface UserRouteGroup {
  store_id: number
  client_id: number
  wk_no: number
  year_no: number | null
  start_date?: string | null
  end_date?: string | null
  client_name: string
  store_name: string
  region_name: string
  city_name: string
  channel_name: string
  chain_name: string
  store_format_name: string
  user_count: number
  category_count: number
  routes: UserRoute[]
}

export interface UserRouteRowInput {
  id?: number
  wk_no: number
  day: WeekDay
  user_id: number
  shift_id: number
  visit_type_id: number
  category_ids?: number[]
  subcat_ids?: number[]
  brand_ids?: number[]
  w_m_bm?: number
}

export interface BulkSaveUserRoutesRequest {
  store_id: number
  client_id: number
  wk_no: number
  year_no?: number
  sync?: boolean
  routes: Omit<UserRouteRowInput, 'wk_no'>[]
}

export interface UserRoutesExcelScope {
  client_id: number
  year_no: number
  wk_no: number
}

class UserRoutesRepository {
  async getAll(
    page: number = 1,
    limit: number = 10,
    filters?: {
      user_id?: number
      store_id?: number
      region_id?: number
      city_id?: number
      client_id?: number
      wk_no?: number
      day?: WeekDay
    },
  ): Promise<PaginatedResponse<UserRouteGroup>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (filters?.user_id != null) params.append('user_id', String(filters.user_id))
    if (filters?.store_id != null) params.append('store_id', String(filters.store_id))
    if (filters?.region_id != null) params.append('region_id', String(filters.region_id))
    if (filters?.city_id != null) params.append('city_id', String(filters.city_id))
    if (filters?.client_id != null) params.append('client_id', String(filters.client_id))
    if (filters?.wk_no != null) params.append('wk_no', String(filters.wk_no))
    if (filters?.day != null) params.append('day', filters.day)
    return apiClient.get<PaginatedResponse<UserRouteGroup>>(`/user-routes?${params.toString()}`)
  }

  async getTenantWeeks(yearNo?: number): Promise<TenantWeek[]> {
    const params = new URLSearchParams()
    if (yearNo != null) params.append('year_no', String(yearNo))
    const qs = params.toString()
    return apiClient.get<TenantWeek[]>(qs ? `/user-routes/tenant-weeks?${qs}` : '/user-routes/tenant-weeks')
  }

  async getById(id: number): Promise<UserRoute> {
    return apiClient.get<UserRoute>(`/user-routes/${id}`)
  }

  async getByGroup(filters: {
    store_id: number
    client_id: number
    wk_no?: number
    day?: WeekDay
  }): Promise<UserRoute[]> {
    const params = new URLSearchParams({
      store_id: String(filters.store_id),
      client_id: String(filters.client_id),
    })
    if (filters.wk_no != null) params.append('wk_no', String(filters.wk_no))
    if (filters.day != null) params.append('day', filters.day)
    return apiClient.get<UserRoute[]>(`/user-routes/group?${params.toString()}`)
  }

  async bulkSave(data: BulkSaveUserRoutesRequest): Promise<UserRoute[]> {
    return apiClient.post<UserRoute[]>('/user-routes/bulk-save', data)
  }

  async delete(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/user-routes/${id}`)
  }

  async exportToExcel(scope: UserRoutesExcelScope): Promise<Blob> {
    const params = new URLSearchParams({
      client_id: String(scope.client_id),
      year_no: String(scope.year_no),
      wk_no: String(scope.wk_no),
    })
    return apiClient.getBlob(`/user-routes/export/excel?${params.toString()}`)
  }

  async exportTemplate(scope: UserRoutesExcelScope): Promise<Blob> {
    const params = new URLSearchParams({
      client_id: String(scope.client_id),
      year_no: String(scope.year_no),
      wk_no: String(scope.wk_no),
    })
    return apiClient.getBlob(`/user-routes/export/template?${params.toString()}`)
  }

  async getImportPreview(scope: UserRoutesExcelScope): Promise<{ existing_count: number }> {
    const params = new URLSearchParams({
      client_id: String(scope.client_id),
      year_no: String(scope.year_no),
      wk_no: String(scope.wk_no),
    })
    return apiClient.get<{ existing_count: number }>(`/user-routes/import/preview?${params.toString()}`)
  }

  async importFromExcel(
    file: File,
    scope: UserRoutesExcelScope,
    confirmReplace = false,
  ): Promise<{
    success: boolean
    message: string
    requires_confirmation?: boolean
    existing_count?: number
    data: {
      success: boolean
      total: number
      created: number
      updated: number
      deleted: number
      errors: Array<{ row: number; message: string }>
    }
  }> {
    const params = new URLSearchParams({
      client_id: String(scope.client_id),
      year_no: String(scope.year_no),
      wk_no: String(scope.wk_no),
    })
    if (confirmReplace) params.append('confirm_replace', '1')
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/user-routes/import/excel?${params.toString()}`, formData)
  }
}

export const userRoutesRepository = new UserRoutesRepository()
